// The site's destinations, in one place, so the account menu in the nav and the
// drawer on Mae's page link to the same routes and neither keeps its own list.

import type { User } from "@/types";
// Relative so the node tests, which resolve no path alias for values, can load it.
import { getRecentSearch } from "../../lib/recent-search-storage";

export const SITE_ROUTES = {
  home: "/",
  search: "/",
  results: "/results",
  savedSearches: "/searches",
  // Saved homes are the second section of the Saved page.
  favorites: "/searches#saved-homes",
  profile: "/profile",
  mae: "/assistant-profile",
  about: "/about",
} as const;

export type SiteNavLink = { key: string; label: string; href: string };

export type RecentSearchLink = { label: string; href: string };

// The buyer's most recent search as a link, or null when there is none. The
// account menu shows it as "Recently viewed"; the drawer's Search entry goes
// there too, so "Search" means their search, not an empty page.
export function recentSearchLink(recent: ReturnType<typeof getRecentSearch>): RecentSearchLink | null {
  const lastQuery = recent?.query || "";
  const criteria = recent?.criteria || null;
  const labelParts = criteria
    ? [
        criteria.city,
        criteria.min_beds ? `${criteria.min_beds}+ bed` : null,
        criteria.max_price ? `under $${Number(criteria.max_price).toLocaleString()}` : null,
      ].filter(Boolean)
    : [];
  const label = labelParts.length > 0 ? labelParts.join(" / ") : lastQuery;
  const query = lastQuery || labelParts.join(" ");
  return query ? { label, href: `${SITE_ROUTES.results}?q=${encodeURIComponent(query)}` } : null;
}

export function recentSearchLinkForUser(user: Pick<User, "id"> | null | undefined): RecentSearchLink | null {
  try {
    return recentSearchLink(getRecentSearch(user));
  } catch {
    return null;
  }
}

// The site links the drawer lists under Mae's sections, in order. One "Saved": the
// buyer's search and their saved homes are one page.
export function siteNavLinks(recentSearchHref: string | null): SiteNavLink[] {
  return [
    { key: "home", label: "Home", href: SITE_ROUTES.home },
    { key: "search", label: "Search", href: recentSearchHref ?? SITE_ROUTES.search },
    { key: "saved", label: "Saved", href: SITE_ROUTES.savedSearches },
    { key: "profile", label: "Profile", href: SITE_ROUTES.profile },
  ];
}
